import { Fetcher } from '@this/src/util';
import React, { useEffect, useMemo, useState } from 'react';
import {
  OrganizationBody,
  OrganizationBodyFooterActions,
  OrganizationTitle
} from '@this/components/organization/organization.style';
import { Loading } from '@this/shared/ui/feedbacks/loading';
import { Button } from '@this/shared/ui/inputs/button';
import { Select } from '@this/shared/ui/inputs/select';

type TaxType = {
  id: number;
  name: string;
  tax_ratio: number;
  freee_tax_code: number | null;
};

type FreeeTaxCode = {
  code: number;
  name_ja: string;
  available: boolean;
};

type Response = {
  tax_types: TaxType[];
  freee_tax_codes: FreeeTaxCode[];
  is_freee_connected: boolean;
};

const TaxTypeMappings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([]);
  const [editingTaxTypes, setEditingTaxTypes] = useState<TaxType[]>([]);
  const [freeeTaxCodes, setFreeeTaxCodes] = useState<FreeeTaxCode[]>([]);
  const [isFreeeConnected, setIsFreeeConnected] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const freeeTaxCodeMap = useMemo(() => {
    return new Map(freeeTaxCodes.map(taxCode => [taxCode.code, taxCode.name_ja]));
  }, [freeeTaxCodes]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await Fetcher.get<Response>('/organization/tax_type_mappings.json');
      setTaxTypes(response.tax_types);
      setEditingTaxTypes(response.tax_types);
      setFreeeTaxCodes(response.freee_tax_codes);
      setIsFreeeConnected(response.is_freee_connected);
    } catch (e) {
      setError('通信エラーが発生しました。時間をおいて再度お試しください。');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const startEdit = () => {
    setSaveError('');
    setEditingTaxTypes(taxTypes.map(taxType => ({ ...taxType })));
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setSaveError('');
    setEditingTaxTypes(taxTypes.map(taxType => ({ ...taxType })));
    setIsEditing(false);
  };

  const saveEdit = () => {
    setSaveError('');
    setSaving(true);
    Fetcher.put<Response>('/organization/tax_type_mappings.json', {
      tax_type_mappings: editingTaxTypes.map(taxType => ({
        tax_type_id: taxType.id,
        freee_tax_code: taxType.freee_tax_code
      }))
    })
      .then(response => {
        setTaxTypes(response.tax_types);
        setEditingTaxTypes(response.tax_types);
        setFreeeTaxCodes(response.freee_tax_codes);
        setIsFreeeConnected(response.is_freee_connected);
        setIsEditing(false);
      })
      .catch(() => {
        setSaveError('通信エラーが発生しました。時間をおいて再度お試しください。');
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const updateEditingTaxType = (taxTypeId: number, freeeTaxCode: string) => {
    setEditingTaxTypes(current =>
      current.map(taxType =>
        taxType.id === taxTypeId
          ? {
              ...taxType,
              freee_tax_code: freeeTaxCode === '' ? null : Number(freeeTaxCode)
            }
          : taxType
      )
    );
  };

  const displayTaxTypes = isEditing ? editingTaxTypes : taxTypes;

  return (
    <>
      <OrganizationTitle>税区分マスタ</OrganizationTitle>
      <OrganizationBody>
        {loading ? (
          <Loading />
        ) : error ? (
          <p>{error}</p>
        ) : (
          <>
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>AI Travel 税区分</th>
                    <th style={styles.th}>freee 税区分</th>
                  </tr>
                </thead>
                <tbody>
                  {displayTaxTypes.map(taxType => (
                    <tr key={taxType.id}>
                      <td style={styles.leftCell}>{taxType.name}</td>
                      <td style={styles.rightCell}>
                        {isEditing ? (
                          isFreeeConnected ? (
                            <Select
                              value={taxType.freee_tax_code ?? ''}
                              onChange={e => updateEditingTaxType(taxType.id, e.target.value)}
                              style={styles.select}
                            >
                              <option value="">-（未設定）</option>
                              {freeeTaxCodes.map(freeeTaxCode => (
                                <option key={freeeTaxCode.code} value={freeeTaxCode.code}>
                                  {freeeTaxCode.name_ja}
                                </option>
                              ))}
                            </Select>
                          ) : (
                            ''
                          )
                        ) : isFreeeConnected ? (
                          freeeTaxCodeMap.get(taxType.freee_tax_code ?? -1) ?? ''
                        ) : (
                          ''
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {saveError && <p>{saveError}</p>}
          </>
        )}
        {!loading && !error && (
          <OrganizationBodyFooterActions style={styles.footerActions}>
            {isEditing ? (
              <>
                <Button onClick={saveEdit} disabled={saving}>
                  保存
                </Button>
                <Button onClick={cancelEdit} disabled={saving}>
                  キャンセル
                </Button>
              </>
            ) : (
              <Button onClick={startEdit}>編集</Button>
            )}
          </OrganizationBodyFooterActions>
        )}
      </OrganizationBody>
    </>
  );
};

const styles = {
  tableWrap: {
    overflowX: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed' as const
  },
  th: {
    textAlign: 'left' as const,
    padding: '8px 12px',
    borderBottom: '1px solid #ddd'
  },
  leftCell: {
    width: '40%',
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
    height: '56px',
    verticalAlign: 'middle' as const
  },
  rightCell: {
    padding: '10px 12px',
    borderBottom: '1px solid #eee',
    height: '56px',
    verticalAlign: 'middle' as const
  },
  select: {
    minWidth: '240px'
  },
  footerActions: {
    minHeight: '52px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  }
} as const;

export default TaxTypeMappings;
